// src/data/usStatesData.js
// Comprehensive SEO & clinical navigation dataset covering all 50 US States + DC.
// Grounded on verified NCI-Designated Comprehensive Cancer Centers, NCI Cancer Centers,
// and premier academic oncology networks across the United States.

export const US_STATES = [
  {
    "name": "Alabama",
    "abbr": "AL",
    "slug": "alabama",
    "capital": "Montgomery",
    "majorCities": [
      "Birmingham",
      "Huntsville",
      "Mobile",
      "Montgomery"
    ],
    "region": "South",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "O'Neal Comprehensive Cancer Center at UAB",
        "city": "Birmingham",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Phase I Clinical Trials",
          "Precision Oncology",
          "CAR-T Cell Therapy",
          "Hematologic Malignancies"
        ],
        "website": "https://www.uab.edu/onealcancercenter"
      }
    ],
    "topNetworks": [
      "UAB Medicine Oncology Network",
      "Huntsville Hospital Cancer Center",
      "USA Health Mitchell Cancer Institute (Mobile)"
    ],
    "highlights": "Home to the O'Neal Comprehensive Cancer Center at UAB, the only NCI-designated cancer center serving Alabama and the central Deep South.",
    "clinicalTrials": "Over 200 active clinical trials across lung, breast, colorectal, and hematologic cancers.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Alabama?",
        "a": "The O'Neal Comprehensive Cancer Center at the University of Alabama at Birmingham (UAB) is Alabama's only NCI-Designated Comprehensive Cancer Center, recognized nationally for research and specialized surgical oncology."
      },
      {
        "q": "Does ByOnco navigate patients to cancer centers in Alabama?",
        "a": "Yes. ByOnco matches patients to leading specialists, helps secure second opinions from UAB oncologists, and navigates insurance authorizations for specialized therapies."
      }
    ]
  },
  {
    "name": "Alaska",
    "abbr": "AK",
    "slug": "alaska",
    "capital": "Juneau",
    "majorCities": [
      "Anchorage",
      "Fairbanks",
      "Juneau"
    ],
    "region": "West",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "Providence Alaska Cancer Center (Anchorage)",
      "Alaska Native Medical Center Oncology",
      "Fred Hutch Cancer Center Regional Network (Affiliate Referral Pathway)"
    ],
    "highlights": "Alaska patients frequently access specialized surgical oncology and Phase I trials through formalized direct referral pipelines to Fred Hutchinson Cancer Center in Seattle.",
    "clinicalTrials": "Regional cooperative trials via SWOG and direct transfer protocols for cellular therapies.",
    "faqs": [
      {
        "q": "Where do Alaska cancer patients go for advanced treatment?",
        "a": "While Providence Alaska Cancer Center and Alaska Native Medical Center deliver frontline chemo and radiation in Anchorage, complex surgical resections and CAR-T trials are coordinated with Fred Hutch Cancer Center in Seattle, WA."
      },
      {
        "q": "How does ByOnco assist cancer patients living in Alaska?",
        "a": "ByOnco coordinates cross-state second opinions, medical travel logistics between Anchorage and Seattle, and lodging navigation for families traveling for complex oncology care."
      }
    ]
  },
  {
    "name": "Arizona",
    "abbr": "AZ",
    "slug": "arizona",
    "capital": "Phoenix",
    "majorCities": [
      "Phoenix",
      "Tucson",
      "Scottsdale",
      "Mesa",
      "Flagstaff"
    ],
    "region": "West",
    "nciCount": 2,
    "nciCenters": [
      {
        "name": "University of Arizona Cancer Center",
        "city": "Tucson",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "GI Cancers",
          "Skin Cancer / Melanoma",
          "Cancer Prevention",
          "Precision Medicine"
        ],
        "website": "https://cancercenter.arizona.edu"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center - Arizona",
        "city": "Phoenix",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Proton Beam Therapy",
          "Surgical Oncology",
          "Neuro-Oncology",
          "CAR-T Therapy"
        ],
        "website": "https://www.mayoclinic.org"
      }
    ],
    "topNetworks": [
      "Banner MD Anderson Cancer Center (Gilbert)",
      "HonorHealth Virginia G. Piper Cancer Center (Scottsdale)",
      "Phoenix Children's Hospital Center for Cancer and Blood Disorders"
    ],
    "highlights": "Arizona boasts world-class oncology powerhouses including Mayo Clinic in Phoenix/Scottsdale and University of Arizona Cancer Center in Tucson, with full access to proton therapy and targeted clinical trials.",
    "clinicalTrials": "Hundreds of open immunotherapy, targeted therapy, and cell therapy trials across Phoenix and Tucson.",
    "faqs": [
      {
        "q": "Which cancer centers in Arizona are NCI-designated?",
        "a": "Arizona has two NCI-Designated Comprehensive Cancer Centers: Mayo Clinic Comprehensive Cancer Center (Phoenix/Scottsdale) and the University of Arizona Cancer Center (Tucson)."
      },
      {
        "q": "Is proton therapy available for cancer patients in Arizona?",
        "a": "Yes, Mayo Clinic in Phoenix provides advanced intensity-modulated proton beam therapy for pediatric, brain, head and neck, and thoracic cancers."
      }
    ]
  },
  {
    "name": "Arkansas",
    "abbr": "AR",
    "slug": "arkansas",
    "capital": "Little Rock",
    "majorCities": [
      "Little Rock",
      "Fayetteville",
      "Fort Smith",
      "Springdale"
    ],
    "region": "South",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Winthrop P. Rockefeller Cancer Institute (UAMS)",
        "city": "Little Rock",
        "designation": "Major Academic Cancer Institute",
        "specialties": [
          "Multiple Myeloma",
          "Bone Marrow Transplant",
          "Surgical Oncology",
          "Hematologic Oncology"
        ],
        "website": "https://cancer.uams.edu"
      }
    ],
    "topNetworks": [
      "UAMS Myeloma Center (World-renowned)",
      "Highlands Oncology Group (Northwest Arkansas)",
      "Arkansas Children's Hospital Oncology"
    ],
    "highlights": "The UAMS Myeloma Center in Little Rock is globally recognized as one of the premier destinations for multiple myeloma research, novel therapies, and autologous transplants.",
    "clinicalTrials": "Extensive clinical trial portfolio with global leadership in plasma cell disorders.",
    "faqs": [
      {
        "q": "What is Arkansas most renowned for in cancer care?",
        "a": "The UAMS Myeloma Center at Winthrop P. Rockefeller Cancer Institute in Little Rock is an international leader in pioneering curative regimens and transplants for multiple myeloma."
      }
    ]
  },
  {
    "name": "California",
    "abbr": "CA",
    "slug": "california",
    "capital": "Sacramento",
    "majorCities": [
      "Los Angeles",
      "San Francisco",
      "San Diego",
      "San Jose",
      "Palo Alto",
      "Sacramento"
    ],
    "region": "West",
    "nciCount": 8,
    "nciCenters": [
      {
        "name": "City of Hope Comprehensive Cancer Center",
        "city": "Duarte",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Bone Marrow Transplant",
          "CAR-T Cell Therapy",
          "Immunotherapy",
          "Surgical Oncology"
        ],
        "website": "https://www.cityofhope.org"
      },
      {
        "name": "Stanford Cancer Institute",
        "city": "Palo Alto",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Cancer Genomics",
          "Immunotherapy",
          "Precision Radiation",
          "Lymphoma"
        ],
        "website": "https://med.stanford.edu/cancer.html"
      },
      {
        "name": "UCSF Helen Diller Family Comprehensive Cancer Center",
        "city": "San Francisco",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Neuro-Oncology",
          "Prostate Cancer",
          "Breast Oncology",
          "Phase I Trials"
        ],
        "website": "https://cancer.ucsf.edu"
      },
      {
        "name": "UCLA Jonsson Comprehensive Cancer Center",
        "city": "Los Angeles",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Targeted Therapy",
          "Melanoma & Immunotherapy",
          "Thoracic Oncology",
          "Gynecologic Oncology"
        ],
        "website": "https://cancer.ucla.edu"
      },
      {
        "name": "UC San Diego Moores Cancer Center",
        "city": "San Diego",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Personalized Medicine",
          "Surgical Oncology",
          "Proton Therapy",
          "GI Oncology"
        ],
        "website": "https://health.ucsd.edu/cancer"
      },
      {
        "name": "USC Norris Comprehensive Cancer Center",
        "city": "Los Angeles",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Urologic Oncology",
          "Epigenetics",
          "Colorectal Cancer"
        ],
        "website": "https://www.cancer.keckmedicine.org"
      },
      {
        "name": "UC Davis Comprehensive Cancer Center",
        "city": "Sacramento",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Comparative Oncology",
          "Robotic Surgery",
          "Genitourinary Oncology"
        ],
        "website": "https://health.ucdavis.edu/cancer"
      },
      {
        "name": "Chao Family Comprehensive Cancer Center (UC Irvine)",
        "city": "Orange",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Early Phase Clinical Trials",
          "Biophotonics",
          "Integrative Oncology"
        ],
        "website": "https://www.ucihealth.org/cancer"
      }
    ],
    "topNetworks": [
      "Cedars-Sinai Samuel Oschin Comprehensive Cancer Institute",
      "Children's Hospital Los Angeles (CHLA)",
      "Scripps MD Anderson Cancer Center (San Diego)",
      "Sutter Health Cancer Centers"
    ],
    "highlights": "California is the largest oncology innovation ecosystem in the United States, with 8 NCI-Designated Comprehensive Cancer Centers pioneering CAR-T therapy, precision genomics, and robotic surgery.",
    "clinicalTrials": "More than 1,200 active clinical trials across Northern and Southern California.",
    "faqs": [
      {
        "q": "What are the top cancer centers in California?",
        "a": "California's highest-ranked cancer institutions include City of Hope (Duarte), Stanford Cancer Institute (Palo Alto), UCSF Helen Diller (San Francisco), UCLA Jonsson, and UC San Diego Moores."
      },
      {
        "q": "How does ByOnco help patients in California?",
        "a": "ByOnco navigates patients to leading subspecialists across the UC system, Stanford, and City of Hope, while providing continuous 24/7 AI-guided oncology support between clinic visits."
      }
    ]
  },
  {
    "name": "Colorado",
    "abbr": "CO",
    "slug": "colorado",
    "capital": "Denver",
    "majorCities": [
      "Denver",
      "Aurora",
      "Colorado Springs",
      "Boulder",
      "Fort Collins"
    ],
    "region": "West",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "University of Colorado Cancer Center",
        "city": "Aurora",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Thoracic Oncology",
          "Targeted Therapy (ALK/EGFR)",
          "Sarcoma",
          "Urologic Oncology"
        ],
        "website": "https://medschool.cuanschutz.edu/colorado-cancer-center"
      }
    ],
    "topNetworks": [
      "UCHealth Anschutz Medical Campus",
      "Sarah Cannon Cancer Institute at HealthONE",
      "Children's Hospital Colorado Center for Cancer and Blood Disorders"
    ],
    "highlights": "CU Cancer Center on the Anschutz Medical Campus is the premier Rocky Mountain destination for groundbreaking lung cancer genomics and targeted therapy trials.",
    "clinicalTrials": "Extensive early-phase precision oncology trials serving Colorado, Wyoming, and Montana.",
    "faqs": [
      {
        "q": "What is the leading cancer center in Colorado?",
        "a": "The University of Colorado Cancer Center in Aurora is the Rocky Mountain region's only NCI-Designated Comprehensive Cancer Center."
      }
    ]
  },
  {
    "name": "Connecticut",
    "abbr": "CT",
    "slug": "connecticut",
    "capital": "Hartford",
    "majorCities": [
      "New Haven",
      "Hartford",
      "Stamford",
      "Bridgeport"
    ],
    "region": "Northeast",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Yale Cancer Center / Smilow Cancer Hospital",
        "city": "New Haven",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Cancer Immunology",
          "Melanoma",
          "Thoracic Oncology",
          "Genomics"
        ],
        "website": "https://www.yalecancercenter.org"
      }
    ],
    "topNetworks": [
      "Hartford HealthCare Cancer Institute (Memorial Sloan Kettering Alliance)",
      "Stamford Health Bennett Cancer Center"
    ],
    "highlights": "Yale Cancer Center and Smilow Cancer Hospital are world leaders in checkpoint inhibitor discovery and immunotherapy research, backed by MSKCC care alliance pathways in Hartford.",
    "clinicalTrials": "Over 350 clinical trials running through Smilow Cancer Hospital.",
    "faqs": [
      {
        "q": "Is Yale Cancer Center NCI-designated?",
        "a": "Yes, Yale Cancer Center is an NCI-Designated Comprehensive Cancer Center and one of the founding institutions of modern cancer immunotherapy."
      }
    ]
  },
  {
    "name": "Delaware",
    "abbr": "DE",
    "slug": "delaware",
    "capital": "Dover",
    "majorCities": [
      "Wilmington",
      "Dover",
      "Newark"
    ],
    "region": "Northeast",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "ChristianaCare Helen F. Graham Cancer Center & Research Institute (Newark)",
      "Nemours Children's Health Center for Cancer and Blood Disorders (Wilmington)",
      "Beebe Healthcare Tunnell Cancer Center"
    ],
    "highlights": "The Helen F. Graham Cancer Center in Newark participates in the NCI Community Oncology Research Program (NCORP) with trial accrual rates that rival major academic centers.",
    "clinicalTrials": "Active NCORP trial participation and collaborative pathways with Philadelphia NCI institutions.",
    "faqs": [
      {
        "q": "Where can Delaware patients get advanced cancer treatment?",
        "a": "ChristianaCare's Helen F. Graham Cancer Center in Newark offers top community oncology, while quaternary care is easily accessible in nearby Philadelphia (Penn Abramson, Fox Chase)."
      }
    ]
  },
  {
    "name": "Florida",
    "abbr": "FL",
    "slug": "florida",
    "capital": "Tallahassee",
    "majorCities": [
      "Miami",
      "Tampa",
      "Orlando",
      "Jacksonville",
      "Fort Lauderdale"
    ],
    "region": "South",
    "nciCount": 3,
    "nciCenters": [
      {
        "name": "Moffitt Cancer Center",
        "city": "Tampa",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Cellular Immunotherapy",
          "Cutaneous Oncology / Melanoma",
          "Thoracic Oncology",
          "Blood and Marrow Transplant"
        ],
        "website": "https://www.moffitt.org"
      },
      {
        "name": "Sylvester Comprehensive Cancer Center (University of Miami)",
        "city": "Miami",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Epigenetics",
          "Precision Oncology",
          "Latin American Cancer Health Disparities",
          "Surgical Oncology"
        ],
        "website": "https://umiamihealth.org/sylvester"
      },
      {
        "name": "Mayo Clinic Comprehensive Cancer Center - Florida",
        "city": "Jacksonville",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Neuro-Oncology",
          "Proton & Carbon Ion Research",
          "Pancreatic Cancer"
        ],
        "website": "https://www.mayoclinic.org"
      }
    ],
    "topNetworks": [
      "UF Health Shands Cancer Hospital (Gainesville)",
      "Orlando Health Cancer Institute",
      "Baptist Health Miami Cancer Institute (Proton Therapy)",
      "Cleveland Clinic Florida (Weston)"
    ],
    "highlights": "Florida is a premier hub for both domestic and international oncology care, featuring Moffitt Cancer Center in Tampa, Sylvester in Miami, and Mayo Clinic in Jacksonville.",
    "clinicalTrials": "Hundreds of early-phase cellular therapy, CAR-T, and targeted drug trials.",
    "faqs": [
      {
        "q": "What is the top-ranked cancer hospital in Florida?",
        "a": "Moffitt Cancer Center in Tampa is consistently ranked among the top cancer hospitals in the nation, alongside Sylvester Comprehensive Cancer Center in Miami and Mayo Clinic Florida in Jacksonville."
      }
    ]
  },
  {
    "name": "Georgia",
    "abbr": "GA",
    "slug": "georgia",
    "capital": "Atlanta",
    "majorCities": [
      "Atlanta",
      "Savannah",
      "Augusta",
      "Columbus",
      "Macon"
    ],
    "region": "South",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Winship Cancer Institute of Emory University",
        "city": "Atlanta",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Multiple Myeloma",
          "Proton Therapy",
          "Lung Cancer",
          "Bone Marrow Transplant",
          "Phase I Trials"
        ],
        "website": "https://winshipcancer.emory.edu"
      }
    ],
    "topNetworks": [
      "Emory Proton Therapy Center (Atlanta)",
      "Northside Hospital Cancer Institute (Leading US BMT program)",
      "Georgia Cancer Center at Augusta University",
      "Children's Healthcare of Atlanta (CHOA) Aflac Cancer & Blood Disorders Center"
    ],
    "highlights": "Georgia's oncology ecosystem in Atlanta includes Winship Cancer Institute of Emory University (Georgia's only NCI-Designated Comprehensive Cancer Center) and Northside Hospital, one of the highest-volume bone marrow transplant centers in America.",
    "clinicalTrials": "Over 300 clinical trials active across Atlanta and Augusta.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Atlanta, Georgia?",
        "a": "Winship Cancer Institute of Emory University is the premier cancer center in Atlanta and the only NCI-Designated Comprehensive Cancer Center in the state of Georgia."
      },
      {
        "q": "Does Atlanta have a proton therapy facility?",
        "a": "Yes, the Emory Proton Therapy Center in Midtown Atlanta provides precision pencil-beam proton radiation for complex tumors."
      }
    ]
  },
  {
    "name": "Hawaii",
    "abbr": "HI",
    "slug": "hawaii",
    "capital": "Honolulu",
    "majorCities": [
      "Honolulu",
      "Hilo",
      "Kailua",
      "Kahului"
    ],
    "region": "West",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "University of Hawaii Cancer Center",
        "city": "Honolulu",
        "designation": "NCI-Designated Cancer Center",
        "specialties": [
          "Cancer Epidemiology",
          "Multi-Ethnic Health Research",
          "Clinical Trials Consortium"
        ],
        "website": "https://www.uhcancercenter.org"
      }
    ],
    "topNetworks": [
      "The Queen's Medical Center Cancer Center (Honolulu)",
      "Hawaii Pacific Health Cancer Centers",
      "Kapi'olani Medical Center for Women & Children"
    ],
    "highlights": "UH Cancer Center conducts critical research into the unique cancer genetics of Pacific Islander, Native Hawaiian, and Asian populations in partnership with Queen's Health.",
    "clinicalTrials": "Consortium trials focused on gastric, liver, and lung cancer therapies.",
    "faqs": [
      {
        "q": "What cancer care is available in Hawaii?",
        "a": "The University of Hawaii Cancer Center and The Queen's Medical Center provide specialized clinical care, clinical trials, and robotic surgical oncology on Oahu."
      }
    ]
  },
  {
    "name": "Idaho",
    "abbr": "ID",
    "slug": "idaho",
    "capital": "Boise",
    "majorCities": [
      "Boise",
      "Meridian",
      "Nampa",
      "Idaho Falls",
      "Pocatello"
    ],
    "region": "West",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "St. Luke's Cancer Institute (Boise)",
      "Saint Alphonsus Cancer Institute (Boise)",
      "Huntsman Cancer Institute Affiliate Network (Salt Lake City partnership)"
    ],
    "highlights": "St. Luke's Cancer Institute is Idaho's largest oncology provider, offering comprehensive radiation and medical oncology with direct referral pipelines to Huntsman Cancer Institute in Utah.",
    "clinicalTrials": "Active participation in clinical trials through national research networks.",
    "faqs": [
      {
        "q": "Where do cancer patients in Idaho receive treatment?",
        "a": "St. Luke's Cancer Institute in Boise handles the majority of state oncology care, while complex Phase I trials and bone marrow transplants are often coordinated with Huntsman Cancer Institute (Salt Lake City) or Fred Hutch (Seattle)."
      }
    ]
  },
  {
    "name": "Illinois",
    "abbr": "IL",
    "slug": "illinois",
    "capital": "Springfield",
    "majorCities": [
      "Chicago",
      "Evanston",
      "Peoria",
      "Rockford",
      "Naperville"
    ],
    "region": "Midwest",
    "nciCount": 2,
    "nciCenters": [
      {
        "name": "Robert H. Lurie Comprehensive Cancer Center of Northwestern University",
        "city": "Chicago",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Precision Medicine",
          "Immunotherapy",
          "Surgical Oncology",
          "Hematologic Oncology"
        ],
        "website": "https://www.cancer.northwestern.edu"
      },
      {
        "name": "University of Chicago Comprehensive Cancer Center",
        "city": "Chicago",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Cellular Therapy / CAR-T",
          "Gastrointestinal Cancers",
          "Genomics",
          "Phase I Trials"
        ],
        "website": "https://cancer.uchicago.edu"
      }
    ],
    "topNetworks": [
      "Rush University Cancer Center (Chicago)",
      "Ann & Robert H. Lurie Children's Hospital of Chicago",
      "University of Illinois Cancer Center",
      "NorthShore University HealthSystem Kellogg Cancer Center"
    ],
    "highlights": "Chicago is a powerhouse of Midwestern oncology, anchored by Northwestern Lurie and University of Chicago, with world-class pediatric cancer care at Lurie Children's.",
    "clinicalTrials": "Over 500 active clinical trials covering rare cancers, CAR-T, and precision targeted drugs.",
    "faqs": [
      {
        "q": "What are the best cancer centers in Chicago, Illinois?",
        "a": "The top cancer centers in Chicago are Northwestern University's Robert H. Lurie Comprehensive Cancer Center and the University of Chicago Comprehensive Cancer Center."
      }
    ]
  },
  {
    "name": "Indiana",
    "abbr": "IN",
    "slug": "indiana",
    "capital": "Indianapolis",
    "majorCities": [
      "Indianapolis",
      "Fort Wayne",
      "South Bend",
      "Bloomington",
      "Evansville"
    ],
    "region": "Midwest",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Indiana University Melvin and Bren Simon Comprehensive Cancer Center",
        "city": "Indianapolis",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Testicular Cancer (World Pioneer)",
          "Breast Cancer / Triple-Negative",
          "Pancreatic Cancer",
          "Hematology"
        ],
        "website": "https://www.cancer.iu.edu"
      }
    ],
    "topNetworks": [
      "IU Health Simon Cancer Center",
      "Community Health Network MD Anderson Cancer Center Partnership",
      "Riley Hospital for Children at IU Health",
      "Parkview Cancer Institute (Fort Wayne)"
    ],
    "highlights": "Indiana University Simon Comprehensive Cancer Center in Indianapolis is internationally famous as the birthplace of curative therapy for testicular cancer under Dr. Lawrence Einhorn.",
    "clinicalTrials": "Renowned clinical research in germ cell tumors, breast cancer genomics, and cellular therapy.",
    "faqs": [
      {
        "q": "What makes Indiana University Simon Cancer Center world-famous?",
        "a": "IU Simon Cancer Center in Indianapolis developed the cisplatin-based curative regimen that transformed testicular cancer from a fatal disease to having a 95%+ cure rate."
      },
      {
        "q": "How does ByOnco guide cancer patients in Indiana?",
        "a": "ByOnco provides direct matching to IU Simon specialists, assists with second opinion record intake, and offers continuous 24/7 symptom and treatment navigation."
      }
    ]
  },
  {
    "name": "Iowa",
    "abbr": "IA",
    "slug": "iowa",
    "capital": "Des Moines",
    "majorCities": [
      "Des Moines",
      "Iowa City",
      "Cedar Rapids",
      "Davenport",
      "Sioux City"
    ],
    "region": "Midwest",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "University of Iowa Holden Comprehensive Cancer Center",
        "city": "Iowa City",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Neuroendocrine Tumors (NETs)",
          "Free Radical Radiation Biology",
          "Sarcoma",
          "Stem Cell Transplant"
        ],
        "website": "https://uihc.org/cancer"
      }
    ],
    "topNetworks": [
      "UI Hospitals & Clinics (Iowa City)",
      "Mission Cancer + Blood (Des Moines)",
      "MercyOne Des Moines Cancer Center"
    ],
    "highlights": "Holden Comprehensive Cancer Center in Iowa City is Iowa's only NCI-designated center and a global destination for neuroendocrine tumor (NET) clinical trials and PRRT treatments.",
    "clinicalTrials": "Extensive trial portfolio with international leadership in radiopharmaceuticals and rare tumors.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Iowa?",
        "a": "Holden Comprehensive Cancer Center at the University of Iowa Hospitals & Clinics in Iowa City is Iowa's premier oncology institution."
      }
    ]
  },
  {
    "name": "Kansas",
    "abbr": "KS",
    "slug": "kansas",
    "capital": "Topeka",
    "majorCities": [
      "Wichita",
      "Overland Park",
      "Kansas City",
      "Topeka",
      "Olathe"
    ],
    "region": "Midwest",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "The University of Kansas Cancer Center",
        "city": "Kansas City",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Drug Discovery",
          "Bone Marrow Transplant",
          "Bladder & Urologic Cancers",
          "Immunotherapy"
        ],
        "website": "https://www.kucancercenter.org"
      }
    ],
    "topNetworks": [
      "KU Medical Center (Kansas City)",
      "Cancer Center of Kansas (Wichita)",
      "Stormont Vail Health Cancer Center (Topeka)"
    ],
    "highlights": "The University of Kansas Cancer Center achieved Comprehensive designation in 2022, providing high-tier academic research and advanced cellular therapies across Kansas and Missouri.",
    "clinicalTrials": "Robust early-phase clinical trial network throughout the Heartland region.",
    "faqs": [
      {
        "q": "Is the University of Kansas Cancer Center an NCI Comprehensive center?",
        "a": "Yes, KU Cancer Center holds the elite NCI-Designated Comprehensive Cancer Center status, serving patients across Kansas and the Kansas City metro."
      }
    ]
  },
  {
    "name": "Kentucky",
    "abbr": "KY",
    "slug": "kentucky",
    "capital": "Frankfort",
    "majorCities": [
      "Louisville",
      "Lexington",
      "Bowling Green",
      "Owensboro",
      "Covington"
    ],
    "region": "South",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "University of Kentucky Markey Cancer Center",
        "city": "Lexington",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Lung Cancer",
          "Colorectal Cancer",
          "Appalachian Health Disparities",
          "Precision Oncology"
        ],
        "website": "https://ukhealthcare.uky.edu/markey-cancer-center"
      }
    ],
    "topNetworks": [
      "UofL Health Brown Cancer Center (Louisville)",
      "Baptist Health Cancer Care",
      "St. Elizabeth Healthcare Cancer Center (Northern KY)"
    ],
    "highlights": "UK Markey Cancer Center earned NCI Comprehensive designation in 2023, spearheading aggressive early screening and cutting-edge immunotherapies for Kentucky patients.",
    "clinicalTrials": "Pioneering Phase I clinical trials at Markey and Brown Cancer Center in Louisville.",
    "faqs": [
      {
        "q": "What is the top cancer center in Kentucky?",
        "a": "The University of Kentucky Markey Cancer Center in Lexington is Kentucky's only NCI-Designated Comprehensive Cancer Center."
      }
    ]
  },
  {
    "name": "Louisiana",
    "abbr": "LA",
    "slug": "louisiana",
    "capital": "Baton Rouge",
    "majorCities": [
      "New Orleans",
      "Baton Rouge",
      "Shreveport",
      "Lafayette"
    ],
    "region": "South",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "LSU Health / LCMC Health Cancer Center (New Orleans)",
      "Ochsner Cancer Institute (New Orleans)",
      "Mary Bird Perkins Cancer Center (Baton Rouge)",
      "Feist-Weiller Cancer Center (Shreveport)"
    ],
    "highlights": "Louisiana boasts specialized regional oncology excellence through Ochsner Cancer Institute and Mary Bird Perkins, with deep expertise in surgical oncology and precision radiation.",
    "clinicalTrials": "High-impact clinical trial networks focused on Gulf Coast cancer types.",
    "faqs": [
      {
        "q": "Where are the leading cancer treatment centers in Louisiana?",
        "a": "Ochsner Cancer Institute and LSU Health LCMC Health Cancer Center in New Orleans, alongside Mary Bird Perkins Cancer Center in Baton Rouge, lead Louisiana in comprehensive cancer care."
      }
    ]
  },
  {
    "name": "Maine",
    "abbr": "ME",
    "slug": "maine",
    "capital": "Augusta",
    "majorCities": [
      "Portland",
      "Lewiston",
      "Bangor",
      "South Portland"
    ],
    "region": "Northeast",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "MaineHealth Cancer Care / Maine Medical Center (Portland)",
      "Northern Light Cancer Care (Brewer/Bangor)",
      "The Jackson Laboratory (Bar Harbor - Basic NCI Center)"
    ],
    "highlights": "Maine patients access top-tier clinical cancer care at Maine Medical Center in Portland, while Bar Harbor's Jackson Laboratory is one of the world's premier genetic cancer research hubs.",
    "clinicalTrials": "Direct clinical trial partnerships with Dana-Farber Cancer Institute in Boston.",
    "faqs": [
      {
        "q": "Where can Maine residents get advanced cancer care?",
        "a": "MaineHealth Cancer Care in Portland provides comprehensive oncology, with specialized quaternary treatments supported by Boston academic centers like Dana-Farber."
      }
    ]
  },
  {
    "name": "Maryland",
    "abbr": "MD",
    "slug": "maryland",
    "capital": "Annapolis",
    "majorCities": [
      "Baltimore",
      "Bethesda",
      "Silver Spring",
      "Rockville",
      "Frederick"
    ],
    "region": "Northeast",
    "nciCount": 2,
    "nciCenters": [
      {
        "name": "Sidney Kimmel Comprehensive Cancer Center at Johns Hopkins",
        "city": "Baltimore",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Immunotherapy Discovery",
          "Pancreatic Cancer",
          "Prostate Cancer",
          "Brain Tumors",
          "Pediatric Oncology"
        ],
        "website": "https://www.hopkinsmedicine.org/kimmel_cancer_center"
      },
      {
        "name": "University of Maryland Marlene and Stewart Greenebaum Comprehensive Cancer Center",
        "city": "Baltimore",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Aromatase Inhibitor Research",
          "CAR-T Therapy",
          "Surgical Oncology",
          "Hematology"
        ],
        "website": "https://www.umms.org/cancer"
      }
    ],
    "topNetworks": [
      "National Institutes of Health (NIH) Clinical Center (Bethesda)",
      "MedStar Franklin Square Cancer Center",
      "Anne Arundel Medical Center DeCesaris Cancer Institute"
    ],
    "highlights": "Maryland is a global epicenter of cancer medicine: Johns Hopkins Kimmel Cancer Center pioneered cancer genetic sequencing and modern checkpoint inhibitors, situated alongside the NIH Clinical Center in Bethesda.",
    "clinicalTrials": "Hundreds of cutting-edge Phase I and first-in-human clinical trials.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Maryland?",
        "a": "Johns Hopkins Sidney Kimmel Comprehensive Cancer Center in Baltimore is globally ranked among the top cancer hospitals, alongside the University of Maryland Greenebaum Cancer Center."
      },
      {
        "q": "How does ByOnco connect patients to Johns Hopkins?",
        "a": "ByOnco assists patients worldwide with expert second opinions from Johns Hopkins oncologists, comprehensive records preparation, and structured treatment roadmap comparisons."
      }
    ]
  },
  {
    "name": "Massachusetts",
    "abbr": "MA",
    "slug": "massachusetts",
    "capital": "Boston",
    "majorCities": [
      "Boston",
      "Cambridge",
      "Worcester",
      "Springfield",
      "Lowell"
    ],
    "region": "Northeast",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Dana-Farber / Harvard Cancer Center",
        "city": "Boston",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Precision Oncology",
          "CAR-T & Cellular Therapy",
          "Pediatric Oncology",
          "Breast & Thoracic Cancers"
        ],
        "website": "https://www.dana-farber.org"
      }
    ],
    "topNetworks": [
      "Massachusetts General Hospital (MGH) Cancer Center (Boston)",
      "Brigham and Women's Hospital (Boston)",
      "Boston Children's Hospital / Dana-Farber Pediatric Cancer",
      "Beth Israel Deaconess Medical Center Cancer Center",
      "UMass Memorial Cancer Center of Excellence (Worcester)"
    ],
    "highlights": "Boston, Massachusetts is the undisputed biopharma and clinical oncology capital of the world. Dana-Farber / Harvard Cancer Center, Mass General, and Boston Children's lead the world in new drug discovery and pediatric cure rates.",
    "clinicalTrials": "More than 1,000 active clinical trials across Dana-Farber, Mass General, and Harvard teaching hospitals.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Massachusetts?",
        "a": "Dana-Farber Cancer Institute and Massachusetts General Hospital Cancer Center in Boston are consistently ranked among the top 3 cancer institutions in the United States."
      },
      {
        "q": "Does ByOnco help patients travel to Boston for cancer treatment?",
        "a": "Yes. ByOnco matches national and international patients to Dana-Farber and MGH oncologists, organizes rapid multidisciplinary second opinions, and coordinates clinical record transfers."
      }
    ]
  },
  {
    "name": "Michigan",
    "abbr": "MI",
    "slug": "michigan",
    "capital": "Lansing",
    "majorCities": [
      "Detroit",
      "Ann Arbor",
      "Grand Rapids",
      "Lansing",
      "Flint"
    ],
    "region": "Midwest",
    "nciCount": 2,
    "nciCenters": [
      {
        "name": "University of Michigan Rogel Cancer Center",
        "city": "Ann Arbor",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Cancer Genetics",
          "Endocrine Oncology",
          "Surgical Oncology",
          "Precision Immunotherapy"
        ],
        "website": "https://www.rogelcancercenter.org"
      },
      {
        "name": "Barbara Ann Karmanos Cancer Institute",
        "city": "Detroit",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Phase I Clinical Trials",
          "Bone Marrow Transplant",
          "Urban Cancer Disparities",
          "Breast Oncology"
        ],
        "website": "https://www.karmanos.org"
      }
    ],
    "topNetworks": [
      "Henry Ford Cancer Institute (Detroit)",
      "Corewell Health Cancer Centers (Grand Rapids/Royal Oak)",
      "C.S. Mott Children's Hospital Oncology (Ann Arbor)"
    ],
    "highlights": "Michigan features two elite NCI Comprehensive Cancer Centers: Rogel Cancer Center in Ann Arbor and Karmanos Cancer Institute in Detroit, supported by Corewell Health and Henry Ford.",
    "clinicalTrials": "Over 400 active clinical trials spanning Michigan's major academic health systems.",
    "faqs": [
      {
        "q": "What are the top cancer centers in Michigan?",
        "a": "The University of Michigan Rogel Cancer Center in Ann Arbor and Barbara Ann Karmanos Cancer Institute in Detroit are Michigan's two NCI-Designated Comprehensive Cancer Centers."
      }
    ]
  },
  {
    "name": "Minnesota",
    "abbr": "MN",
    "slug": "minnesota",
    "capital": "St. Paul",
    "majorCities": [
      "Minneapolis",
      "St. Paul",
      "Rochester",
      "Duluth",
      "Bloomington"
    ],
    "region": "Midwest",
    "nciCount": 2,
    "nciCenters": [
      {
        "name": "Mayo Clinic Comprehensive Cancer Center",
        "city": "Rochester",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Multidisciplinary Team Care",
          "Proton Beam Therapy",
          "Surgical Innovation",
          "CAR-T Therapy",
          "Rare Cancers"
        ],
        "website": "https://www.mayoclinic.org"
      },
      {
        "name": "Masonic Cancer Center, University of Minnesota",
        "city": "Minneapolis",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Umbilical Cord Blood & Bone Marrow Transplant",
          "Immunotherapy",
          "Cancer Causes & Prevention"
        ],
        "website": "https://cancer.umn.edu"
      }
    ],
    "topNetworks": [
      "M Health Fairview Cancer Care (Twin Cities)",
      "Children's Minnesota Cancer and Blood Disorders",
      "Park Nicollet Jane Brattain Breast Center"
    ],
    "highlights": "Minnesota is home to Mayo Clinic in Rochester\u2014consistently ranked the #1 hospital in the United States\u2014as well as the Masonic Cancer Center, the birthplace of modern bone marrow transplantation.",
    "clinicalTrials": "Global leader in cutting-edge clinical trials, rare cancer discovery, and advanced radiation therapy.",
    "faqs": [
      {
        "q": "Why is Mayo Clinic Rochester famous for cancer treatment?",
        "a": "Mayo Clinic in Rochester, MN brings together world-leading specialists across all oncologic disciplines for integrated, same-week diagnosis, genomic profiling, and customized treatment plans."
      },
      {
        "q": "How does ByOnco help patients seeking care at Mayo Clinic?",
        "a": "ByOnco assists patients with medical appointment requests, insurance and self-pay cost estimation, and rapid record transfers for Mayo Clinic evaluations."
      }
    ]
  },
  {
    "name": "Mississippi",
    "abbr": "MS",
    "slug": "mississippi",
    "capital": "Jackson",
    "majorCities": [
      "Jackson",
      "Gulfport",
      "Southaven",
      "Biloxi",
      "Hattiesburg"
    ],
    "region": "South",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "University of Mississippi Medical Center (UMMC) Cancer Center and Research Institute (Jackson)",
      "Memorial Hospital Cancer Center (Gulfport)",
      "St. Dominic Cancer Center (Jackson)"
    ],
    "highlights": "UMMC Cancer Center in Jackson is the state's academic medical hub, partnering with regional networks to expand access to early cancer detection and targeted chemotherapy.",
    "clinicalTrials": "Community clinical trials and statewide cancer registry integration.",
    "faqs": [
      {
        "q": "Where can Mississippi cancer patients receive specialized care?",
        "a": "The UMMC Cancer Center and Research Institute in Jackson provides the state's most comprehensive medical and surgical oncology services."
      }
    ]
  },
  {
    "name": "Missouri",
    "abbr": "MO",
    "slug": "missouri",
    "capital": "Jefferson City",
    "majorCities": [
      "Kansas City",
      "St. Louis",
      "Springfield",
      "Columbia",
      "Independence"
    ],
    "region": "Midwest",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Siteman Cancer Center",
        "city": "St. Louis",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Proton Therapy",
          "Bone Marrow Transplant",
          "Genomics",
          "Phase I Clinical Trials"
        ],
        "website": "https://siteman.wustl.edu"
      }
    ],
    "topNetworks": [
      "Barnes-Jewish Hospital & Washington University School of Medicine (St. Louis)",
      "Saint Louis University Cancer Center",
      "Ellis Fischel Cancer Center at MU Health Care (Columbia)",
      "Children's Mercy Kansas City Oncology"
    ],
    "highlights": "Siteman Cancer Center at Barnes-Jewish Hospital and Washington University School of Medicine in St. Louis treats more than 12,000 newly diagnosed cancer patients annually.",
    "clinicalTrials": "Over 600 clinical trials active at Siteman and Washington University.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Missouri?",
        "a": "Siteman Cancer Center at Barnes-Jewish Hospital and Washington University in St. Louis is Missouri's only NCI-Designated Comprehensive Cancer Center."
      }
    ]
  },
  {
    "name": "Montana",
    "abbr": "MT",
    "slug": "montana",
    "capital": "Helena",
    "majorCities": [
      "Billings",
      "Missoula",
      "Great Falls",
      "Bozeman",
      "Helena"
    ],
    "region": "West",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "Billings Clinic Cancer Center (Billings)",
      "Providence St. Patrick Hospital Cancer Center (Missoula)",
      "Bozeman Health Cancer Center",
      "Benefis Sletten Cancer Institute (Great Falls)"
    ],
    "highlights": "Billings Clinic Cancer Center is a member of the Mayo Clinic Care Network, offering Montana patients local expert oncology backed by Mayo Clinic specialist consultations.",
    "clinicalTrials": "Regional clinical trials through Montana Cancer Consortium (NCORP).",
    "faqs": [
      {
        "q": "How do cancer patients in Montana access top-tier specialists?",
        "a": "Billings Clinic Cancer Center provides comprehensive regional care through its Mayo Clinic Care Network affiliation, while complex surgical oncology can be coordinated with Fred Hutch (Seattle) or Huntsman (Salt Lake City)."
      }
    ]
  },
  {
    "name": "Nebraska",
    "abbr": "NE",
    "slug": "nebraska",
    "capital": "Lincoln",
    "majorCities": [
      "Omaha",
      "Lincoln",
      "Bellevue",
      "Grand Island"
    ],
    "region": "Midwest",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Fred & Pamela Buffett Cancer Center",
        "city": "Omaha",
        "designation": "NCI-Designated Cancer Center",
        "specialties": [
          "Lymphoma",
          "Leukemia & Stem Cell Transplant",
          "Pancreatic Cancer",
          "Genomics"
        ],
        "website": "https://www.unmc.edu/cancercenter"
      }
    ],
    "topNetworks": [
      "Nebraska Medicine (Omaha)",
      "Bryan Health Cancer Center (Lincoln)",
      "Methodist Estabrook Cancer Center (Omaha)"
    ],
    "highlights": "The Fred & Pamela Buffett Cancer Center at Nebraska Medicine in Omaha is globally recognized for pioneering research in lymphoma and hematologic transplants.",
    "clinicalTrials": "Over 200 active clinical trials in hematology and solid tumor oncology.",
    "faqs": [
      {
        "q": "What is Nebraska's premier cancer institute?",
        "a": "The Fred & Pamela Buffett Cancer Center at Nebraska Medicine / UNMC in Omaha is the state's sole NCI-Designated Cancer Center."
      }
    ]
  },
  {
    "name": "Nevada",
    "abbr": "NV",
    "slug": "nevada",
    "capital": "Carson City",
    "majorCities": [
      "Las Vegas",
      "Reno",
      "Henderson",
      "North Las Vegas",
      "Sparks"
    ],
    "region": "West",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "Comprehensive Cancer Centers of Nevada (Las Vegas - The US Oncology Network)",
      "Renown Health Cancer Institute (Reno)",
      "Steinberg Diagnostic Medical Imaging & Cancer Care",
      "Cure 4 The Kids Foundation (Las Vegas - Pediatric Oncology)"
    ],
    "highlights": "Comprehensive Cancer Centers of Nevada in Las Vegas offers extensive Phase I-IV trials in affiliation with UCLA, providing frontline care while coordinating quaternary cases with Southern California NCI centers.",
    "clinicalTrials": "Clinical trials through The US Oncology Network and UCLA research partnerships.",
    "faqs": [
      {
        "q": "Where do cancer patients go for treatment in Las Vegas, Nevada?",
        "a": "Comprehensive Cancer Centers of Nevada (affiliated with The US Oncology Network) and Renown Health in Reno provide primary cancer treatment, with direct referral connections to UCLA and City of Hope."
      }
    ]
  },
  {
    "name": "New Hampshire",
    "abbr": "NH",
    "slug": "new-hampshire",
    "capital": "Concord",
    "majorCities": [
      "Manchester",
      "Nashua",
      "Concord",
      "Dover",
      "Lebanon"
    ],
    "region": "Northeast",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Dartmouth Cancer Center",
        "city": "Lebanon",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Immunotherapy",
          "Surgical Oncology",
          "Cancer Nanotechnology",
          "Rural Oncology"
        ],
        "website": "https://cancer.dartmouth.edu"
      }
    ],
    "topNetworks": [
      "Dartmouth Hitchcock Medical Center (Lebanon)",
      "Elliot Hospital Solinsky Center for Cancer Care (Manchester)",
      "Catholic Medical Center Oncology"
    ],
    "highlights": "Dartmouth Cancer Center (formerly Norris Cotton) in Lebanon is one of only three NCI-designated comprehensive centers in northern New England, leading national models in rural oncology delivery.",
    "clinicalTrials": "Broad clinical trial portfolio covering northern New England communities.",
    "faqs": [
      {
        "q": "What is the top cancer center in New Hampshire?",
        "a": "Dartmouth Cancer Center at Dartmouth Hitchcock Medical Center in Lebanon is New Hampshire's only NCI-Designated Comprehensive Cancer Center."
      }
    ]
  },
  {
    "name": "New Jersey",
    "abbr": "NJ",
    "slug": "new-jersey",
    "capital": "Trenton",
    "majorCities": [
      "Newark",
      "Jersey City",
      "Paterson",
      "New Brunswick",
      "Cherry Hill"
    ],
    "region": "Northeast",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Rutgers Cancer Institute of New Jersey",
        "city": "New Brunswick",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Cellular Therapy & CAR-T",
          "Genomics",
          "Precision Radiation",
          "Pediatric Oncology"
        ],
        "website": "https://www.cinj.org"
      }
    ],
    "topNetworks": [
      "RWJBarnabas Health Oncology Network",
      "John Theurer Cancer Center at Hackensack Meridian Health",
      "Morristown Medical Center / Atlantic Health System Cancer Care"
    ],
    "highlights": "Rutgers Cancer Institute of New Jersey in New Brunswick, in partnership with RWJBarnabas Health and the Jack & Sheryl Morris Cancer Center, is New Jersey's only NCI-Designated Comprehensive Cancer Center.",
    "clinicalTrials": "Hundreds of clinical trials running across Rutgers CINJ and John Theurer Cancer Center in Hackensack.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in New Jersey?",
        "a": "Rutgers Cancer Institute of New Jersey in New Brunswick is the state's sole NCI-Designated Comprehensive Cancer Center, partnered with RWJBarnabas Health and John Theurer Cancer Center."
      }
    ]
  },
  {
    "name": "New Mexico",
    "abbr": "NM",
    "slug": "new-mexico",
    "capital": "Santa Fe",
    "majorCities": [
      "Albuquerque",
      "Las Cruces",
      "Rio Rancho",
      "Santa Fe",
      "Roswell"
    ],
    "region": "West",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "University of New Mexico Comprehensive Cancer Center",
        "city": "Albuquerque",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Genomics",
          "Multi-Ethnic Cancer Biology",
          "Leukemia",
          "Phase I Clinical Trials"
        ],
        "website": "https://cancer.unm.edu"
      }
    ],
    "topNetworks": [
      "UNM Hospital (Albuquerque)",
      "Presbyterian MD Anderson Cancer Network",
      "CHRISTUS St. Vincent Regional Cancer Center (Santa Fe)"
    ],
    "highlights": "UNM Comprehensive Cancer Center in Albuquerque is the official cancer center of New Mexico, providing cutting-edge genomic sequencing and clinical trials for Hispanic and American Indian populations.",
    "clinicalTrials": "Lead institution for the New Mexico Cancer Care Alliance trial network.",
    "faqs": [
      {
        "q": "What is the leading cancer center in New Mexico?",
        "a": "The University of New Mexico Comprehensive Cancer Center in Albuquerque is the state's only NCI-Designated Comprehensive Cancer Center."
      }
    ]
  },
  {
    "name": "New York",
    "abbr": "NY",
    "slug": "new-york",
    "capital": "Albany",
    "majorCities": [
      "New York City",
      "Buffalo",
      "Rochester",
      "Albany",
      "Syracuse"
    ],
    "region": "Northeast",
    "nciCount": 6,
    "nciCenters": [
      {
        "name": "Memorial Sloan Kettering Cancer Center (MSKCC)",
        "city": "New York City",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Immunotherapy",
          "Surgical Oncology",
          "Rare Cancers",
          "Precision Medicine",
          "Pediatric Oncology"
        ],
        "website": "https://www.mskcc.org"
      },
      {
        "name": "NYU Langone Perlmutter Cancer Center",
        "city": "New York City",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Melanoma",
          "Thoracic Oncology",
          "Pancreatic Cancer",
          "Blood Malignancies"
        ],
        "website": "https://nyulangone.org/locations/perlmutter-cancer-center"
      },
      {
        "name": "Herbert Irving Comprehensive Cancer Center (Columbia University)",
        "city": "New York City",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Cancer Genomics",
          "Precision Oncology",
          "Breast & Prostate Cancers"
        ],
        "website": "https://www.cancer.columbia.edu"
      },
      {
        "name": "The Tisch Cancer Institute at Mount Sinai",
        "city": "New York City",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Liver Cancer (HCC)",
          "Multiple Myeloma",
          "Bladder Cancer",
          "CAR-T Therapy"
        ],
        "website": "https://www.mountsinai.org/care/cancer"
      },
      {
        "name": "Roswell Park Comprehensive Cancer Center",
        "city": "Buffalo",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Immunotherapy",
          "Robotic Surgery",
          "Cellular Therapies",
          "Photodynamic Therapy"
        ],
        "website": "https://www.roswellpark.org"
      },
      {
        "name": "Albert Einstein Cancer Center (Montefiore)",
        "city": "Bronx",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Stem Cell Biology",
          "Urban Health Disparities",
          "Surgical Oncology"
        ],
        "website": "https://www.einsteinmed.edu/centers/cancer"
      }
    ],
    "topNetworks": [
      "NewYork-Presbyterian Hospital (Weill Cornell & Columbia)",
      "Northwell Health Cancer Institute",
      "Wilmot Cancer Institute at University of Rochester"
    ],
    "highlights": "New York is a titan of global oncology, home to Memorial Sloan Kettering (the world's oldest and largest private cancer center), NYU Perlmutter, Columbia Herbert Irving, Mount Sinai Tisch, and Roswell Park in Buffalo.",
    "clinicalTrials": "Over 1,500 active clinical trials across New York City and Upstate New York.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in New York?",
        "a": "Memorial Sloan Kettering Cancer Center (MSKCC) in New York City is internationally recognized as one of the very best cancer hospitals in the world, alongside NYU Langone, Columbia, Mount Sinai, and Roswell Park in Buffalo."
      },
      {
        "q": "How does ByOnco help patients in New York?",
        "a": "ByOnco matches patients to top subspecialists at MSKCC, NYU, and Columbia, coordinates fast-track virtual second opinions, and provides ongoing AI-guided symptom checks."
      }
    ]
  },
  {
    "name": "North Carolina",
    "abbr": "NC",
    "slug": "north-carolina",
    "capital": "Raleigh",
    "majorCities": [
      "Charlotte",
      "Raleigh",
      "Durham",
      "Chapel Hill",
      "Winston-Salem",
      "Greensboro"
    ],
    "region": "South",
    "nciCount": 3,
    "nciCenters": [
      {
        "name": "Duke Cancer Institute",
        "city": "Durham",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Brain Tumors (Preston Robert Tisch)",
          "Breast Cancer",
          "Immunotherapy",
          "Urologic Oncology"
        ],
        "website": "https://www.dukecancerinstitute.org"
      },
      {
        "name": "UNC Lineberger Comprehensive Cancer Center",
        "city": "Chapel Hill",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Cellular Immunotherapy / CAR-T",
          "Genomics",
          "Breast Cancer Subtyping",
          "Pediatric Oncology"
        ],
        "website": "https://unclineberger.org"
      },
      {
        "name": "Atrium Health Wake Forest Baptist Comprehensive Cancer Center",
        "city": "Winston-Salem",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Brain & Spine Tumors",
          "Leukemia & Lymphoma",
          "Precision Radiation / Gamma Knife"
        ],
        "website": "https://www.wakehealth.edu"
      }
    ],
    "topNetworks": [
      "Atrium Health Levine Cancer Institute (Charlotte)",
      "Novant Health Cancer Institute",
      "Preston Robert Tisch Brain Tumor Center at Duke"
    ],
    "highlights": "North Carolina's Research Triangle is an elite oncology corridor, featuring Duke Cancer Institute, UNC Lineberger, and Wake Forest Baptist, alongside Charlotte's Levine Cancer Institute.",
    "clinicalTrials": "Hundreds of cutting-edge clinical trials, notably Duke's breakthrough polio virus brain tumor trials and Lineberger's academic CAR-T center.",
    "faqs": [
      {
        "q": "What are the top cancer centers in North Carolina?",
        "a": "North Carolina has three elite NCI-Designated Comprehensive Cancer Centers: Duke Cancer Institute in Durham, UNC Lineberger in Chapel Hill, and Atrium Health Wake Forest Baptist in Winston-Salem."
      }
    ]
  },
  {
    "name": "North Dakota",
    "abbr": "ND",
    "slug": "north-dakota",
    "capital": "Bismarck",
    "majorCities": [
      "Fargo",
      "Bismarck",
      "Grand Forks",
      "Minot"
    ],
    "region": "Midwest",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "Sanford Roger Maris Cancer Center (Fargo)",
      "Sanford Bismarck Oncology Center",
      "Essentia Health Cancer Center (Fargo)",
      "Altru Cancer Center (Grand Forks)"
    ],
    "highlights": "The Sanford Roger Maris Cancer Center in Fargo is the flagship oncology destination for the Dakotas and western Minnesota, delivering comprehensive radiation, chemotherapy, and clinical trials.",
    "clinicalTrials": "Active participant in NCORP community clinical trials and collaborative research networks.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in North Dakota?",
        "a": "Sanford Roger Maris Cancer Center in Fargo is North Dakota's premier cancer facility, recognized for comprehensive outpatient and inpatient medical and radiation oncology."
      },
      {
        "q": "How does ByOnco assist cancer patients in North Dakota?",
        "a": "ByOnco provides North Dakota families with access to virtual second opinions from top national NCI centers (such as Mayo Clinic and Fred Hutch) while coordinating localized care in Fargo and Bismarck."
      }
    ]
  },
  {
    "name": "Ohio",
    "abbr": "OH",
    "slug": "ohio",
    "capital": "Columbus",
    "majorCities": [
      "Columbus",
      "Cleveland",
      "Cincinnati",
      "Toledo",
      "Akron",
      "Dayton"
    ],
    "region": "Midwest",
    "nciCount": 3,
    "nciCenters": [
      {
        "name": "The James Cancer Hospital and Solove Research Institute (OSUCCC)",
        "city": "Columbus",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Leukemia & Hematology",
          "Precision Genetics",
          "CAR-T Therapy",
          "Thoracic Oncology"
        ],
        "website": "https://cancer.osu.edu"
      },
      {
        "name": "Case Comprehensive Cancer Center",
        "city": "Cleveland",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Immunotherapy",
          "Cancer Imaging",
          "Stem Cell Biology",
          "Translational Therapeutics"
        ],
        "website": "https://case.edu/cancer"
      },
      {
        "name": "Cleveland Clinic Taussig Cancer Institute",
        "city": "Cleveland",
        "designation": "Leading Cancer Institute (Consortium Partner)",
        "specialties": [
          "Proton Therapy",
          "Surgical Oncology",
          "Genitourinary Oncology",
          "Phase I Trials"
        ],
        "website": "https://my.clevelandclinic.org/departments/cancer"
      }
    ],
    "topNetworks": [
      "University Hospitals Seidman Cancer Center (Cleveland)",
      "Cincinnati Children's Hospital Medical Center Cancer Institute",
      "Nationwide Children's Hospital Oncology (Columbus)",
      "TriHealth Cancer Institute (Cincinnati)"
    ],
    "highlights": "Ohio is a powerhouse of clinical oncology: Cleveland Clinic Taussig and Case Western lead in cancer surgery and imaging, The James in Columbus is one of America's largest freestanding cancer hospitals, and Cincinnati Children's is a global pediatric oncology leader.",
    "clinicalTrials": "Over 800 active clinical trials across Cleveland, Columbus, and Cincinnati.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Ohio?",
        "a": "Ohio is anchored by the world-renowned Cleveland Clinic Taussig Cancer Institute and The Ohio State University Comprehensive Cancer Center - Arthur G. James Cancer Hospital in Columbus."
      },
      {
        "q": "How does ByOnco guide patients in Ohio?",
        "a": "ByOnco connects Ohio patients with leading oncologists at Cleveland Clinic and OSU James, coordinates second opinion evaluations, and offers 24/7 empathetic oncology navigation."
      }
    ]
  },
  {
    "name": "Oklahoma",
    "abbr": "OK",
    "slug": "oklahoma",
    "capital": "Oklahoma City",
    "majorCities": [
      "Oklahoma City",
      "Tulsa",
      "Norman",
      "Broken Arrow",
      "Edmond"
    ],
    "region": "South",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Stephenson Cancer Center at OU Health",
        "city": "Oklahoma City",
        "designation": "NCI-Designated Cancer Center",
        "specialties": [
          "Phase I Clinical Trials",
          "Gynecologic Oncology",
          "Proton Therapy",
          "Tobacco Research"
        ],
        "website": "https://www.stephensoncancercenter.org"
      }
    ],
    "topNetworks": [
      "OU Health Stephenson Cancer Center",
      "Oklahoma Proton Center (Oklahoma City)",
      "Saint Francis Health System Cancer Center (Tulsa)",
      "Integris Health Cancer Institute"
    ],
    "highlights": "Stephenson Cancer Center at OU Health is Oklahoma's only NCI-designated center, consistently ranking in the top tier nationwide for patient accrual to NCI National Clinical Trials Network studies.",
    "clinicalTrials": "Major national leader in Phase I early-phase clinical trial enrollment.",
    "faqs": [
      {
        "q": "What is the best cancer center in Oklahoma?",
        "a": "The Stephenson Cancer Center at the University of Oklahoma Health in Oklahoma City is Oklahoma's sole NCI-Designated Cancer Center."
      }
    ]
  },
  {
    "name": "Oregon",
    "abbr": "OR",
    "slug": "oregon",
    "capital": "Salem",
    "majorCities": [
      "Portland",
      "Eugene",
      "Salem",
      "Gresham",
      "Hillsboro",
      "Bend"
    ],
    "region": "West",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "OHSU Knight Cancer Institute",
        "city": "Portland",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Early Cancer Detection",
          "Precision Oncology",
          "Targeted Therapy (Gleevec)",
          "Hematology"
        ],
        "website": "https://www.ohsu.edu/knight-cancer-institute"
      }
    ],
    "topNetworks": [
      "Providence Cancer Institute (Portland - Franz Clinic)",
      "Legacy Cancer Institute (Portland)",
      "Samaritan Cancer Program (Corvallis)"
    ],
    "highlights": "OHSU Knight Cancer Institute in Portland, directed by Dr. Brian Druker (pioneer of Gleevec / imatinib), is internationally famed for pioneering targeted cancer therapy and early tumor detection technologies.",
    "clinicalTrials": "Over 400 active clinical trials with world leadership in targeted genomics and immunotherapies.",
    "faqs": [
      {
        "q": "What is Oregon's top cancer center?",
        "a": "The OHSU Knight Cancer Institute in Portland is Oregon's only NCI-Designated Comprehensive Cancer Center, globally famous for developing targeted therapies for leukemia and GIST."
      },
      {
        "q": "How does ByOnco support Oregon cancer patients?",
        "a": "ByOnco matches patients to OHSU Knight specialists, helps coordinate precision genomic profiling interpretations, and provides 24/7 clinical AI support between appointments."
      }
    ]
  },
  {
    "name": "Pennsylvania",
    "abbr": "PA",
    "slug": "pennsylvania",
    "capital": "Harrisburg",
    "majorCities": [
      "Philadelphia",
      "Pittsburgh",
      "Allentown",
      "Erie",
      "Reading",
      "Scranton"
    ],
    "region": "Northeast",
    "nciCount": 4,
    "nciCenters": [
      {
        "name": "Abramson Cancer Center, University of Pennsylvania",
        "city": "Philadelphia",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "CAR-T Cell Therapy Birthplace",
          "Proton Therapy",
          "Immunotherapy",
          "Breast & Ovarian Genetics (BRCA)"
        ],
        "website": "https://www.pennmedicine.org/cancer"
      },
      {
        "name": "UPMC Hillman Cancer Center",
        "city": "Pittsburgh",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Immunotherapy & Melanoma",
          "Surgical Oncology",
          "Cancer Virology",
          "Stereotactic Radiosurgery"
        ],
        "website": "https://hillman.upmc.com"
      },
      {
        "name": "Fox Chase Cancer Center (Temple Health)",
        "city": "Philadelphia",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Urologic Oncology",
          "Gastrointestinal Cancers",
          "Genomics",
          "Radiation Oncology"
        ],
        "website": "https://www.foxchase.org"
      },
      {
        "name": "Sidney Kimmel Cancer Center at Jefferson",
        "city": "Philadelphia",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Prostate Cancer",
          "Precision Medicine",
          "Melanoma & Rare Tumors"
        ],
        "website": "https://www.jeffersonhealth.org/clinical-specialties/cancer"
      }
    ],
    "topNetworks": [
      "Children's Hospital of Philadelphia (CHOP - World's premier pediatric oncology center)",
      "Penn State Health Cancer Institute (Hershey)",
      "Allegheny Health Network Cancer Institute (Pittsburgh)",
      "Geisinger Cancer Institute (Danville)"
    ],
    "highlights": "Pennsylvania is home to historic breakthroughs in cancer medicine: Penn's Abramson Cancer Center invented the world's first FDA-approved CAR-T cell therapy (Kymriah), and CHOP is globally revered for curing pediatric leukemias.",
    "clinicalTrials": "Over 1,000 active clinical trials across Philadelphia and Pittsburgh.",
    "faqs": [
      {
        "q": "What are the best cancer centers in Pennsylvania?",
        "a": "Pennsylvania's top cancer centers include Penn Medicine's Abramson Cancer Center, UPMC Hillman in Pittsburgh, Fox Chase Cancer Center, Sidney Kimmel Cancer Center at Jefferson, and Children's Hospital of Philadelphia (CHOP)."
      },
      {
        "q": "Where was CAR-T cell therapy invented?",
        "a": "CAR-T therapy was developed at the University of Pennsylvania's Abramson Cancer Center and Children's Hospital of Philadelphia under Dr. Carl June."
      }
    ]
  },
  {
    "name": "Rhode Island",
    "abbr": "RI",
    "slug": "rhode-island",
    "capital": "Providence",
    "majorCities": [
      "Providence",
      "Warwick",
      "Cranston",
      "Pawtucket"
    ],
    "region": "Northeast",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "Lifespan Cancer Institute at Rhode Island Hospital (Providence)",
      "Brown University Legorreta Cancer Center",
      "Women & Infants Hospital Breast & Gynecologic Oncology"
    ],
    "highlights": "The Legorreta Cancer Center at Brown University and Lifespan Cancer Institute provide academic cancer therapies in Providence with direct linkages to Harvard/Dana-Farber networks in nearby Boston.",
    "clinicalTrials": "Translational clinical trials through Brown University Health.",
    "faqs": [
      {
        "q": "Where can Rhode Island cancer patients get top treatment?",
        "a": "The Lifespan Cancer Institute at Rhode Island Hospital and Brown University's Legorreta Cancer Center in Providence provide advanced multi-specialty cancer care."
      }
    ]
  },
  {
    "name": "South Carolina",
    "abbr": "SC",
    "slug": "south-carolina",
    "capital": "Columbia",
    "majorCities": [
      "Charleston",
      "Columbia",
      "Greenville",
      "Mount Pleasant",
      "Rock Hill"
    ],
    "region": "South",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "MUSC Hollings Cancer Center",
        "city": "Charleston",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Immunotherapy",
          "Surgical Oncology",
          "Cancer Disparities",
          "Blood & Marrow Transplant"
        ],
        "website": "https://hollingscancercenter.musc.edu"
      }
    ],
    "topNetworks": [
      "Prisma Health Cancer Institute (Greenville/Columbia)",
      "Gibbs Cancer Center & Research Institute (Spartanburg)",
      "Roper St. Francis Cancer Center (Charleston)"
    ],
    "highlights": "MUSC Hollings Cancer Center in Charleston is South Carolina's only NCI-Designated Comprehensive Cancer Center, offering statewide clinical trial access and mobile cancer screening units.",
    "clinicalTrials": "Over 200 clinical trials running across Charleston, Columbia, and Greenville.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in South Carolina?",
        "a": "MUSC Hollings Cancer Center in Charleston is South Carolina's only NCI-Designated Comprehensive Cancer Center."
      },
      {
        "q": "How does ByOnco support patients in South Carolina?",
        "a": "ByOnco matches patients with Hollings Cancer Center oncologists, facilitates second opinions, and provides 24/7 clinical AI answers for treatment questions."
      }
    ]
  },
  {
    "name": "South Dakota",
    "abbr": "SD",
    "slug": "south-dakota",
    "capital": "Pierre",
    "majorCities": [
      "Sioux Falls",
      "Rapid City",
      "Aberdeen",
      "Brookings"
    ],
    "region": "Midwest",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "Avera Cancer Institute (Sioux Falls)",
      "Sanford Cancer Center (Sioux Falls)",
      "Monument Health Cancer Care Institute (Rapid City)"
    ],
    "highlights": "Sioux Falls is the regional medical hub of the Northern Plains: Avera Cancer Institute and Sanford Cancer Center offer advanced genomic sequencing, surgical oncology, and bone marrow transplants.",
    "clinicalTrials": "Active Phase I and precision oncology clinical trial programs at Avera and Sanford.",
    "faqs": [
      {
        "q": "What are the top cancer hospitals in South Dakota?",
        "a": "Avera Cancer Institute and Sanford Cancer Center in Sioux Falls are the leading cancer treatment centers in South Dakota and the Upper Midwest."
      },
      {
        "q": "How does ByOnco help patients in South Dakota?",
        "a": "ByOnco connects patients across South Dakota to clinical specialists in Sioux Falls and top national cancer centers (such as Mayo Clinic Rochester), assisting with second opinions and 24/7 symptom tracking."
      }
    ]
  },
  {
    "name": "Tennessee",
    "abbr": "TN",
    "slug": "tennessee",
    "capital": "Nashville",
    "majorCities": [
      "Nashville",
      "Memphis",
      "Knoxville",
      "Chattanooga",
      "Clarksville"
    ],
    "region": "South",
    "nciCount": 2,
    "nciCenters": [
      {
        "name": "Vanderbilt-Ingram Cancer Center",
        "city": "Nashville",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Precision Medicine",
          "Cancer Genomics",
          "Immunotherapy",
          "Surgical Oncology"
        ],
        "website": "https://www.vicc.org"
      },
      {
        "name": "St. Jude Children's Research Hospital",
        "city": "Memphis",
        "designation": "NCI-Designated Comprehensive Cancer Center (Pediatric)",
        "specialties": [
          "Pediatric Leukemia & Lymphoma",
          "Pediatric Brain Tumors",
          "Gene Therapy",
          "BMT"
        ],
        "website": "https://www.stjude.org"
      }
    ],
    "topNetworks": [
      "Sarah Cannon Cancer Institute (Nashville - Global Cancer Research Hub)",
      "University of Tennessee Medical Center Cancer Institute (Knoxville)",
      "West Cancer Center & Research Institute (Memphis)"
    ],
    "highlights": "Tennessee holds legendary oncology standing: St. Jude Children's Research Hospital in Memphis (families never receive a bill for treatment) and Vanderbilt-Ingram Cancer Center in Nashville, the pioneer of targeted oncogenomics.",
    "clinicalTrials": "Over 700 clinical trials across Vanderbilt-Ingram and Sarah Cannon Research Institute.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Tennessee?",
        "a": "Vanderbilt-Ingram Cancer Center in Nashville is the premier adult NCI Comprehensive center, while St. Jude Children's Research Hospital in Memphis is world-renowned for pediatric cancers."
      }
    ]
  },
  {
    "name": "Texas",
    "abbr": "TX",
    "slug": "texas",
    "capital": "Austin",
    "majorCities": [
      "Houston",
      "Dallas",
      "San Antonio",
      "Austin",
      "Fort Worth",
      "El Paso"
    ],
    "region": "South",
    "nciCount": 4,
    "nciCenters": [
      {
        "name": "The University of Texas MD Anderson Cancer Center",
        "city": "Houston",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "All Cancer Types",
          "Immunotherapy",
          "Proton Therapy",
          "Phase I Trials",
          "Surgical Oncology"
        ],
        "website": "https://www.mdanderson.org"
      },
      {
        "name": "UT Southwestern Harold C. Simmons Comprehensive Cancer Center",
        "city": "Dallas",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Kidney Cancer",
          "Stereotactic Radiation",
          "Genomics",
          "Phase I Clinical Trials"
        ],
        "website": "https://utswmed.org/cancer"
      },
      {
        "name": "Mays Cancer Center at UT Health San Antonio",
        "city": "San Antonio",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Hispanic Cancer Health",
          "Breast Cancer Discovery",
          "Surgical Oncology"
        ],
        "website": "https://cancer.uthscsa.edu"
      },
      {
        "name": "Dan L Duncan Comprehensive Cancer Center at Baylor College of Medicine",
        "city": "Houston",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Breast Cancer Genetics",
          "Pediatric Oncology",
          "Immunotherapy"
        ],
        "website": "https://www.bcm.edu/dan-l-duncan-comprehensive-cancer-center"
      }
    ],
    "topNetworks": [
      "Texas Children's Cancer and Hematology Center (Houston - Largest US pediatric program)",
      "Methodist Hospital Cancer Center (Houston)",
      "Baylor Scott & White Charles A. Sammons Cancer Center (Dallas)",
      "Dell Children's Blood and Cancer Center (Austin)"
    ],
    "highlights": "Texas is home to the world's largest medical complex, the Texas Medical Center in Houston, anchored by MD Anderson Cancer Center\u2014consistently ranked the #1 cancer hospital in the world\u2014and UT Southwestern in Dallas.",
    "clinicalTrials": "More than 2,000 active clinical trials, attracting patients from all 50 states and over 100 countries.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Texas and the world?",
        "a": "The University of Texas MD Anderson Cancer Center in Houston is widely considered the world's leading cancer treatment and research institution, alongside UT Southwestern Simmons in Dallas."
      },
      {
        "q": "How does ByOnco help patients traveling to MD Anderson in Houston?",
        "a": "ByOnco helps patients navigate appointment requests, review multidisciplinary treatment plans, estimate treatment and lodging costs, and communicate seamlessly with clinical teams."
      }
    ]
  },
  {
    "name": "Utah",
    "abbr": "UT",
    "slug": "utah",
    "capital": "Salt Lake City",
    "majorCities": [
      "Salt Lake City",
      "West Valley City",
      "Provo",
      "West Jordan",
      "Orem"
    ],
    "region": "West",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Huntsman Cancer Institute at the University of Utah",
        "city": "Salt Lake City",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Hereditary Cancer Genetics",
          "Sarcoma",
          "Melanoma",
          "Proton Therapy",
          "Bone Marrow Transplant"
        ],
        "website": "https://healthcare.utah.edu/huntsmancancerinstitute"
      }
    ],
    "topNetworks": [
      "Intermountain Cancer Centers (Intermountain Health)",
      "Primary Children's Hospital Oncology (Salt Lake City)"
    ],
    "highlights": "Huntsman Cancer Institute in Salt Lake City holds the world's largest genetic cancer database (the Utah Population Database), leading global discoveries in inherited cancer mutations (including BRCA1).",
    "clinicalTrials": "Over 300 active trials serving patients throughout Utah, Idaho, Wyoming, Montana, and Nevada.",
    "faqs": [
      {
        "q": "What is the premier cancer center in Utah?",
        "a": "Huntsman Cancer Institute at the University of Utah in Salt Lake City is the Mountain West's premier NCI-Designated Comprehensive Cancer Center."
      }
    ]
  },
  {
    "name": "Vermont",
    "abbr": "VT",
    "slug": "vermont",
    "capital": "Montpelier",
    "majorCities": [
      "Burlington",
      "South Burlington",
      "Rutland",
      "Barre"
    ],
    "region": "Northeast",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "University of Vermont Cancer Center (Burlington)",
      "Central Vermont Medical Center Cancer Care",
      "Rutland Regional Cancer Center"
    ],
    "highlights": "The University of Vermont Cancer Center in Burlington is an academic clinical center delivering high-tier surgical oncology, immunotherapy, and cancer clinical trials.",
    "clinicalTrials": "Academic trials coordinated through the University of Vermont Larner College of Medicine.",
    "faqs": [
      {
        "q": "What is the top cancer center in Vermont?",
        "a": "The University of Vermont Cancer Center at UVM Medical Center in Burlington is Vermont's flagship academic cancer facility."
      }
    ]
  },
  {
    "name": "Virginia",
    "abbr": "VA",
    "slug": "virginia",
    "capital": "Richmond",
    "majorCities": [
      "Virginia Beach",
      "Norfolk",
      "Richmond",
      "Arlington",
      "Alexandria",
      "Charlottesville"
    ],
    "region": "South",
    "nciCount": 2,
    "nciCenters": [
      {
        "name": "VCU Massey Comprehensive Cancer Center",
        "city": "Richmond",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Community-Engaged Cancer Research",
          "Hematologic Oncology",
          "Cellular Therapy",
          "Surgical Oncology"
        ],
        "website": "https://www.masseycancercenter.org"
      },
      {
        "name": "UVA Comprehensive Cancer Center",
        "city": "Charlottesville",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Focused Ultrasound",
          "Immunotherapy",
          "Melanoma",
          "Thoracic Oncology"
        ],
        "website": "https://uvahealth.com/services/cancer-care"
      }
    ],
    "topNetworks": [
      "Inova Schar Cancer Institute (Fairfax - Northern Virginia)",
      "Sentara Cancer Network (Norfolk/Virginia Beach)",
      "Carilion Clinic Cancer Center (Roanoke)"
    ],
    "highlights": "Virginia features two elite NCI Comprehensive Cancer Centers: VCU Massey in Richmond and UVA in Charlottesville, alongside Northern Virginia's Inova Schar Cancer Institute.",
    "clinicalTrials": "Hundreds of clinical trials, including groundbreaking focused ultrasound oncology trials at UVA.",
    "faqs": [
      {
        "q": "What are the best cancer centers in Virginia?",
        "a": "Virginia is anchored by VCU Massey Comprehensive Cancer Center in Richmond and UVA Comprehensive Cancer Center in Charlottesville, supported by Inova Schar in Northern Virginia."
      }
    ]
  },
  {
    "name": "Washington",
    "abbr": "WA",
    "slug": "washington",
    "capital": "Olympia",
    "majorCities": [
      "Seattle",
      "Spokane",
      "Tacoma",
      "Vancouver",
      "Bellevue",
      "Everett"
    ],
    "region": "West",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Fred Hutchinson Cancer Center",
        "city": "Seattle",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Bone Marrow Transplant Birthplace",
          "Immunotherapy & CAR-T",
          "Precision Genomics",
          "Proton Therapy"
        ],
        "website": "https://www.fredhutch.org"
      }
    ],
    "topNetworks": [
      "Seattle Children's Hospital Cancer and Blood Disorders Center",
      "UW Medicine / Montlake (Seattle)",
      "MultiCare Regional Cancer Center (Tacoma)",
      "Providence Cancer Center (Spokane)"
    ],
    "highlights": "Fred Hutchinson Cancer Center in Seattle is a global beacon of hope: the birthplace of bone marrow transplantation under Nobel laureate Dr. E. Donnall Thomas and a pioneer of modern cellular immunotherapies.",
    "clinicalTrials": "Over 800 active clinical trials covering the entire Pacific Northwest and Alaska.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Seattle and Washington state?",
        "a": "Fred Hutchinson Cancer Center (in partnership with UW Medicine and Seattle Children's) is the premier cancer center in the Pacific Northwest."
      },
      {
        "q": "How does ByOnco connect patients to Fred Hutch?",
        "a": "ByOnco coordinates expert second opinions with Fred Hutch oncologists, assists with medical record compilation, and provides 24/7 AI-guided treatment guidance."
      }
    ]
  },
  {
    "name": "Washington DC",
    "abbr": "DC",
    "slug": "washington-dc",
    "capital": "Washington",
    "majorCities": [
      "Washington"
    ],
    "region": "Northeast",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "Georgetown Lombardi Comprehensive Cancer Center",
        "city": "Washington",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Breast Cancer",
          "Cancer Prevention",
          "Proton Therapy",
          "Immunotherapy"
        ],
        "website": "https://www.lombardi.georgetown.edu"
      }
    ],
    "topNetworks": [
      "Children's National Hospital Center for Cancer and Blood Disorders",
      "MedStar Washington Hospital Center Cancer Institute",
      "George Washington University Cancer Center"
    ],
    "highlights": "Washington DC is anchored by Georgetown Lombardi Comprehensive Cancer Center and Children's National Hospital, providing world-class capital region oncology and pediatric cancer cure protocols.",
    "clinicalTrials": "Extensive clinical trial portfolio backed by national research consortia.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in Washington DC?",
        "a": "Georgetown Lombardi Comprehensive Cancer Center is the District of Columbia's only NCI-Designated Comprehensive Cancer Center."
      }
    ]
  },
  {
    "name": "West Virginia",
    "abbr": "WV",
    "slug": "west-virginia",
    "capital": "Charleston",
    "majorCities": [
      "Charleston",
      "Huntington",
      "Morgantown",
      "Parkersburg",
      "Wheeling"
    ],
    "region": "South",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "WVU Cancer Institute / J.W. Ruby Memorial Hospital (Morgantown)",
      "Edwards Comprehensive Cancer Center at Cabell Huntington Hospital (Huntington)",
      "CAMC Cancer Center (Charleston)"
    ],
    "highlights": "WVU Cancer Institute in Morgantown is the flagship cancer program for West Virginia, offering advanced robotic surgery, precision radiation, and mobile mammography units.",
    "clinicalTrials": "Statewide clinical trial network addressing Appalachian cancer priorities.",
    "faqs": [
      {
        "q": "What is the best cancer hospital in West Virginia?",
        "a": "WVU Cancer Institute at J.W. Ruby Memorial Hospital in Morgantown is the state's leading academic cancer center."
      }
    ]
  },
  {
    "name": "Wisconsin",
    "abbr": "WI",
    "slug": "wisconsin",
    "capital": "Madison",
    "majorCities": [
      "Milwaukee",
      "Madison",
      "Green Bay",
      "Kenosha",
      "Racine"
    ],
    "region": "Midwest",
    "nciCount": 1,
    "nciCenters": [
      {
        "name": "University of Wisconsin Carbone Cancer Center",
        "city": "Madison",
        "designation": "NCI-Designated Comprehensive Cancer Center",
        "specialties": [
          "Precision Imaging (PET/CT)",
          "Immunotherapy",
          "Surgical Oncology",
          "Hematologic Malignancies"
        ],
        "website": "https://cancer.wisc.edu"
      }
    ],
    "topNetworks": [
      "Froedtert & Medical College of Wisconsin Cancer Center (Milwaukee)",
      "Children's Wisconsin MACC Fund Center for Cancer and Blood Disorders",
      "Aurora Cancer Care"
    ],
    "highlights": "UW Carbone Cancer Center in Madison was one of the nation's original six comprehensive cancer centers, recognized for pioneering tamoxifen and 5-FU fluorouracil chemotherapy.",
    "clinicalTrials": "Over 350 active clinical trials across UW Carbone and Froedtert MCW in Milwaukee.",
    "faqs": [
      {
        "q": "What is the top cancer center in Wisconsin?",
        "a": "The University of Wisconsin Carbone Cancer Center in Madison is Wisconsin's only NCI-Designated Comprehensive Cancer Center, complemented by Froedtert & MCW in Milwaukee."
      }
    ]
  },
  {
    "name": "Wyoming",
    "abbr": "WY",
    "slug": "wyoming",
    "capital": "Cheyenne",
    "majorCities": [
      "Cheyenne",
      "Casper",
      "Laramie",
      "Gillette",
      "Jackson"
    ],
    "region": "West",
    "nciCount": 0,
    "nciCenters": [],
    "topNetworks": [
      "Cheyenne Regional Medical Center Cancer Center (Cheyenne)",
      "Rocky Mountain Oncology (Casper/Lander)",
      "St. John's Health Oncology (Jackson)",
      "Huntsman Cancer Institute Affiliate Network"
    ],
    "highlights": "Wyoming patients receive dedicated community oncology in Cheyenne and Casper, with formalized referral pathways to Huntsman Cancer Institute (Salt Lake City) and CU Cancer Center (Denver).",
    "clinicalTrials": "Access to national trials via regional affiliations with Huntsman and UCHealth.",
    "faqs": [
      {
        "q": "Where do Wyoming cancer patients go for treatment?",
        "a": "Cheyenne Regional Cancer Center and Rocky Mountain Oncology provide local care, while complex surgical cases and cellular therapies are coordinated with nearby NCI centers in Denver (CU Cancer Center) and Salt Lake City (Huntsman)."
      }
    ]
  }
];

export const US_STATES_BY_SLUG = US_STATES.reduce((acc, state) => {
  acc[state.slug] = state;
  return acc;
}, {});

// Prominent US Metro cities mapping with city-level highlights
export const US_METRO_CITIES = {
  atlanta: { stateSlug: 'georgia', cityName: 'Atlanta', featuredCenter: "Winship Cancer Institute of Emory University" },
  seattle: { stateSlug: 'washington', cityName: 'Seattle', featuredCenter: "Fred Hutchinson Cancer Center" },
  boston: { stateSlug: 'massachusetts', cityName: 'Boston', featuredCenter: "Dana-Farber Cancer Institute" },
  houston: { stateSlug: 'texas', cityName: 'Houston', featuredCenter: "MD Anderson Cancer Center" },
  chicago: { stateSlug: 'illinois', cityName: 'Chicago', featuredCenter: "Robert H. Lurie Comprehensive Cancer Center" },
  philadelphia: { stateSlug: 'pennsylvania', cityName: 'Philadelphia', featuredCenter: "Penn Abramson Cancer Center" },
  baltimore: { stateSlug: 'maryland', cityName: 'Baltimore', featuredCenter: "Johns Hopkins Sidney Kimmel Cancer Center" },
  cleveland: { stateSlug: 'ohio', cityName: 'Cleveland', featuredCenter: "Case Comprehensive Cancer Center / Cleveland Clinic" },
  portland: { stateSlug: 'oregon', cityName: 'Portland', featuredCenter: "OHSU Knight Cancer Institute" },
  indianapolis: { stateSlug: 'indiana', cityName: 'Indianapolis', featuredCenter: "Indiana University Melvin and Bren Simon Comprehensive Cancer Center" },
  'new-york-city': { stateSlug: 'new-york', cityName: 'New York City', featuredCenter: "Memorial Sloan Kettering Cancer Center" },
  losangeles: { stateSlug: 'california', cityName: 'Los Angeles', featuredCenter: "UCLA Jonsson Comprehensive Cancer Center" },
  'los-angeles': { stateSlug: 'california', cityName: 'Los Angeles', featuredCenter: "UCLA Jonsson Comprehensive Cancer Center" },
  miami: { stateSlug: 'florida', cityName: 'Miami', featuredCenter: "Sylvester Comprehensive Cancer Center" },
  phoenix: { stateSlug: 'arizona', cityName: 'Phoenix', featuredCenter: "Mayo Clinic Comprehensive Cancer Center" },
  denver: { stateSlug: 'colorado', cityName: 'Denver', featuredCenter: "University of Colorado Cancer Center" },
};

Object.entries(US_METRO_CITIES).forEach(([citySlug, config]) => {
  const baseState = US_STATES_BY_SLUG[config.stateSlug];
  if (baseState) {
    US_STATES_BY_SLUG[citySlug] = {
      ...baseState,
      cityHighlight: config.cityName,
      featuredCenterName: config.featuredCenter,
    };
  }
});

export function getUSStateOrMetro(slugOrCode) {
  if (!slugOrCode) return null;
  const clean = slugOrCode.trim().toLowerCase().replace(/[\s_]+/g, '-');
  return US_STATES_BY_SLUG[clean] || null;
}

export const US_REGIONS = {
  Northeast: US_STATES.filter((s) => s.region === 'Northeast'),
  South: US_STATES.filter((s) => s.region === 'South'),
  Midwest: US_STATES.filter((s) => s.region === 'Midwest'),
  West: US_STATES.filter((s) => s.region === 'West'),
};

export const TOTAL_US_NCI_CENTERS = US_STATES.reduce((sum, s) => sum + s.nciCount, 0);
